import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import i18next from 'i18next'

import { error, success } from 'src/components/system_wide/notification'

import axios from 'src/lib/axios'

const { t } = i18next

const initialState = {
  total: 0,
  appointments: [],
  all_appointments: [],
  show_form: false,
  current_appointment: {
    id: null,
    code: '',
    created_by: null,
    created_by_name: '',
    dealer_id: null,
    piva: '',
    business_name: '',
    date: '',
    address: '',
    brand_id: null,
    brand_name: '',
    place_id: '',
    note: '',
    contacts: [],
    photos: [],
    merchandise_category_id: null,
    created_at: '',
    updated_at: '',
    contract: null,
    dealer_brand: null,
    status: '',
  },
  categories: [],
  pending: false,
}

export const getAppointments = createAsyncThunk(
  'appointments/list',
  async (
    { offset, limit, sortData, filter, filtered_brands, filtered_users, filtered_statuses, filtered_appointment_types },
    thunkApi
  ) => {
    try {
      let { total: currentTotal } = thunkApi.getState().appointments
      if (offset > currentTotal) return

      let { sort_column, sort_direction } = sortData
      let response = await axios({
        url: `/api/appointments`,
        params: {
          offset,
          limit,
          sort_column,
          sort_direction,
          filter,
          filtered_brands: filtered_brands.join(','),
          filtered_users: filtered_users.join(','),
          filtered_statuses: filtered_statuses.join(','),
          filtered_appointment_types: filtered_appointment_types.join(','),
        },
      })
      let { total, appointments } = response.data
      return { total, appointments, reset: offset === 0 }
    } catch (err) {
      error(t('store.appointments.get_appointments_error', 'Impossibile recuperare lista visite'))
      return thunkApi.rejectWithValue()
    }
  }
)

export const createAppointment = createAsyncThunk('appointments/create', async ({ appointment }, thunkApi) => {
  try {
    await axios({
      url: `/api/appointments`,
      method: 'post',
      data: { ...appointment },
    })
    success(t('store.appointments.create_appointment_success', 'Visita creata con successo'))
    thunkApi.dispatch(resetCurrentAppointment())
    thunkApi.dispatch(toggleShowForm())
  } catch (err) {
    error(t('store.appointments.create_appointment_error', 'Impossibile creare la visita'))
    thunkApi.dispatch(resetCurrentAppointment())
    thunkApi.dispatch(toggleShowForm())
    return thunkApi.rejectWithValue()
  }
})

export const updateAppointment = createAsyncThunk('appointments/update', async ({ appointment }, thunkApi) => {
  try {
    await axios({
      url: `/api/appointments/${appointment.id}`,
      method: 'patch',
      data: { ...appointment },
    })
    success(t('store.appointments.update_appointment_success', 'Visita aggiornata con successo'))
    thunkApi.dispatch(resetCurrentAppointment())
    thunkApi.dispatch(toggleShowForm())
  } catch (err) {
    error(t('store.appointments.update_appointment_error', 'Impossibile aggiornare la visita'))
    thunkApi.dispatch(resetCurrentAppointment())
    thunkApi.dispatch(toggleShowForm())
    return thunkApi.rejectWithValue()
  }
})

export const deleteAppointment = createAsyncThunk('appointments/delete', async ({ id }, thunkApi) => {
  try {
    await axios({ url: `/api/appointments/${id}`, method: 'delete' })
    success(t('store.appointments.delete_appointment_success', 'Visita eliminata con successo'))
    thunkApi.dispatch(resetCurrentAppointment())
  } catch (err) {
    error(t('store.appointments.delete_appointment_error', 'Impossibile eliminare la visita'))
    return thunkApi.rejectWithValue()
  }
})

export const changeStatusAppointment = createAsyncThunk(
  'appointments/changeStatus',
  async ({ id, status, rejected_note, date }, thunkApi) => {
    try {
      await axios({ url: `/api/appointments/${id}/status`, method: 'patch', data: { status, rejected_note, date } })
      success(t('store.appointments.change_status_success', 'Stato cambiato con successo'))
    } catch (err) {
      error(t('store.appointments.change_status_error', 'Impossibile cambiare lo stato'))
      return thunkApi.rejectWithValue()
    }
  }
)

export const getAllCategories = createAsyncThunk('appointments/getAllCategories', async thunkApi => {
  try {
    let response = await axios({ url: `/api/categories/all`, method: 'GET' })
    return response.data.categories
  } catch (err) {
    error(t('store.appointments.get_appointments_error', 'Impossibile recuperare lista visite'))
    return thunkApi.rejectWithValue()
  }
})

export const getAllAppointments = createAsyncThunk(
  'appointments/getAllAppointments',
  async ({ filtered_brands, filtered_users, filtered_statuses, filtered_regions, filtered_provinces }, thunkApi) => {
    try {
      let response = await axios({
        url: `/api/appointments/all`,
        method: 'GET',
        params: {
          filtered_brands: filtered_brands.join(','),
          filtered_users: filtered_users.join(','),
          filtered_statuses: filtered_statuses.join(','),
          filtered_regions: encodeURIComponent(filtered_regions.join(',')),
          filtered_provinces: encodeURIComponent(filtered_provinces.join(',')),
        },
      })
      return response.data
    } catch (err) {
      console.log(err)
      error(t('store.appointments.get_appointments_error', 'Impossibile recuperare lista visite'))
      return thunkApi.rejectWithValue()
    }
  }
)

export const appointmentsSlice = createSlice({
  name: 'appointments',
  initialState,

  reducers: {
    setCurrentAppointment: (state, action) => {
      let {
        id,
        type,
        code,
        created_by,
        dealer_brand_id,
        date,
        note,
        photos,
        dealer_brand,
        contract,
        status,
        rejected_note,
        rejected_at,
        success_at,
      } = action.payload
      let { dealer, brand, ...rest } = dealer_brand

      state.current_appointment = {
        id,
        dealer_brand_id,
        code,
        type,
        created_by: created_by.id,
        created_by_name: created_by.first_name + ' ' + created_by.last_name,
        dealer_id: dealer.id,
        piva: dealer.piva,
        business_name: dealer.business_name,
        date,
        note,
        photos,
        contract,
        brand_id: brand.id,
        brand_name: brand.name,
        place_id: dealer.place_id,
        address: dealer.address,
        merchandise_category_id: dealer.merchandise_category_id,
        contacts: dealer.contacts,
        status,
        rejected_note,
        rejected_at,
        success_at,

        dealer_brand: rest,
      }
    },
    toggleShowForm: state => {
      state.show_form = !state.show_form
    },
    resetCurrentAppointment: state => {
      state.current_appointment = initialState.current_appointment
    },
    resetAppointments: () => initialState,
  },

  extraReducers: {
    [getAppointments.fulfilled]: (state, action) => {
      if (!action?.payload) return
      let { total, appointments } = action.payload
      state.total = total
      state.appointments = action.payload.reset ? [...appointments] : [...state.appointments, ...appointments]
    },
    [getAppointments.rejected]: state => {
      state.total = initialState.total
      state.appointments = initialState.appointments
    },

    [getAllCategories.fulfilled]: (state, action) => {
      state.categories = action.payload
    },
    [createAppointment.fulfilled]: (state, action) => {
      state.pending = false
    },
    [createAppointment.rejected]: state => {
      state.pending = false
    },
    [createAppointment.pending]: state => {
      state.pending = true
    },
    [updateAppointment.fulfilled]: state => {
      state.pending = false
    },
    [updateAppointment.rejected]: state => {
      state.pending = false
    },
    [updateAppointment.pending]: state => {
      state.pending = true
    },
    [deleteAppointment.fulfilled]: state => {
      state.pending = false
    },
    [deleteAppointment.rejected]: state => {
      state.pending = false
    },
    [deleteAppointment.pending]: state => {
      state.pending = true
    },
    [changeStatusAppointment.fulfilled]: state => {
      state.pending = false
    },
    [changeStatusAppointment.rejected]: state => {
      state.pending = false
    },
    [changeStatusAppointment.pending]: state => {
      state.pending = true
    },
    [getAllAppointments.fulfilled]: (state, action) => {
      if (!action?.payload) return
      let { appointments } = action.payload
      state.all_appointments = appointments
    },
    [getAllAppointments.rejected]: state => {
      state.all_appointments = initialState.all_appointments
    },
  },
})

export const { setCurrentAppointment, resetCurrentAppointment, toggleShowForm, resetAppointments } =
  appointmentsSlice.actions
export default appointmentsSlice.reducer
